/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef, useState } from 'preact/hooks';
import type {
  GanttCurrentRowLabel,
  GanttCurrentTask,
  GanttProps,
  GanttRowData,
  GanttTaskData
} from './gantt.types';
import { GanttContainer } from './GanttContainer';
import { GanttWithRowAxisLayout } from './GanttWithRowAxisLayout';
import { GanttRowAxis } from './GanttRowAxis';
import { GanttView } from './GanttView';
import { useGanttLayout } from './hooks/useGanttLayout';
import { useTimeComponentEvents } from '#PRIVATE_TimeComponent/hooks/useTimeComponentEvents';
import { useGanttKeyboard } from './hooks/useGanttKeyboard';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils';

/**
 * A gantt displays scheduling information graphically, making it easier to plan, coordinate, and track various tasks and resources.
 */
export function Gantt<
  K1 extends string | number,
  D1 extends GanttRowData<K1, D1, D2>,
  K2 extends string | number,
  D2 extends GanttTaskData<K2>
>({ width, height, testId, ...props }: GanttProps<K2, D1, D2>) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollPosition,
    panZoomLayout,
    withRowAxisLayout,
    getTimeAxesLayout,
    zoomParameters,
    rowDataLayout,
    rowAxisLayout,
    getResolvedViewportLayout,
    getHorizontalScrollbarLayout,
    onHorizontalScrollChange,
    getVerticalScrollbarLayout,
    onVerticalScrollChange,
    isRTL,
    layoutContent,
    isValidConfig,
    isReady
  } = useGanttLayout<K1, K2, D1, D2>(props);

  const ariaProps = {
    'aria-label': props['aria-label'],
    'aria-describedby': props['aria-describedby'],
    'aria-labelledby': props['aria-labelledby'],
    role: 'application' as const
  };

  const isViewportDefined = props.viewportStart !== undefined && props.viewportEnd !== undefined;

  const [ariaActive, setAriaActive] = useState<
    { dataId: K2; ariaId: string; type: 'task' } | { dataId: K1; ariaId: string; type: 'rowLabel' }
  >();
  const [currentTaskInteraction, setCurrentTaskInteraction] = useState<'hover' | 'focus' | 'none'>(
    'none'
  );
  const [hoveredTaskId, setHoveredTaskId] = useState<K2>();
  const onTaskHoverChange = (detail: { id?: K2 }) => {
    detail.id !== undefined &&
      setAriaActive({ dataId: detail.id, ariaId: getRandomId(), type: 'task' });
    setCurrentTaskInteraction(detail.id !== undefined ? 'hover' : 'none');
    setHoveredTaskId(detail.id);
  };
  const [currentRowLabelInteraction, setCurrentRowLabelInteraction] = useState<
    'hover' | 'focus' | 'none'
  >('none');
  const [hoveredRowLabelId, setHoveredRowLabelId] = useState<K1>();
  const onRowLabelHoverChange = (detail: { id?: K1 }) => {
    detail.id !== undefined &&
      setAriaActive({ dataId: detail.id, ariaId: getRandomId(), type: 'rowLabel' });
    setCurrentRowLabelInteraction(detail.id !== undefined ? 'hover' : 'none');
    setHoveredRowLabelId(detail.id);
  };
  const [currentTask, setCurrentTask] = useState<GanttCurrentTask<K2>['currentTask']>();
  const onCurrentTaskChange = (detail: { id: K2; isFocused: boolean }) => {
    if (detail.isFocused) {
      const e = new CustomEvent('KbPanTaskIntoView', { detail: { id: detail.id } });
      visRef.current?.dispatchEvent(e);
      setAriaActive({ dataId: detail.id, ariaId: getRandomId(), type: 'task' });
      setCurrentTaskInteraction('focus');
    }
    setCurrentTask(detail);
  };
  const [currentRowLabel, setCurrentRowLabel] =
    useState<GanttCurrentRowLabel<K1>['currentRowLabel']>();
  const onCurrentRowLabelChange = (detail: { id: K1; isFocused: boolean }) => {
    if (detail.isFocused) {
      const e = new CustomEvent('KbPanRowIntoView', { detail: { id: detail.id } });
      visRef.current?.dispatchEvent(e);
      setAriaActive({ dataId: detail.id, ariaId: getRandomId(), type: 'rowLabel' });
      setCurrentRowLabelInteraction('focus');
    }
    setCurrentRowLabel(detail);
  };

  const visRef = useRef<HTMLDivElement>(null);
  const rowAxisRef = useRef<HTMLDivElement>(null);
  const overlayControlsContainerRef = useRef<HTMLDivElement>(null);
  const eventProps = useTimeComponentEvents({
    rootRef: ref,
    overlayControlsContainerRef,
    isDisabled: !isViewportDefined
  });
  const keyboardProps = useGanttKeyboard({
    visRef,
    rowAxisRef,
    navigationModes: {
      tasks: { currentItem: currentTask, onCurrentItemChange: onCurrentTaskChange },
      rowLabels: { currentItem: currentRowLabel, onCurrentItemChange: onCurrentRowLabelChange }
    },
    rowDataLayout: rowDataLayout!,
    selectionProps: {
      selectedIds: props.selectedIds,
      selectionMode: props.selectionMode,
      onSelectionChange: props.onSelectionChange
    },
    isRTL,
    isDisabled: !isViewportDefined
  });

  // TODO: Move check to util and call in corepack to decide whether to render invalid data state?
  if (!isReady || !isValidConfig) {
    return (
      <GanttContainer innerRef={ref} width={width} height={height} {...ariaProps}>
        {layoutContent}
      </GanttContainer>
    );
  }

  const ganttView = (
    <GanttView
      visRef={visRef}
      overlayControlsContainerRef={overlayControlsContainerRef}
      isRTL={isRTL}
      isViewportDefined={isViewportDefined}
      scrollPosition={scrollPosition!}
      onScrollPositionChange={props.onScrollPositionChange}
      onViewportChange={props.onViewportChange}
      rowAxisSeparator={!!withRowAxisLayout}
      zoomParameters={zoomParameters}
      zooming={props.zooming}
      panZoomLayout={panZoomLayout!}
      getTimeAxesLayout={getTimeAxesLayout!}
      getHorizontalScrollbarLayout={getHorizontalScrollbarLayout!}
      onHorizontalScrollChange={onHorizontalScrollChange!}
      getVerticalScrollbarLayout={getVerticalScrollbarLayout!}
      onVerticalScrollChange={onVerticalScrollChange!}
      getResolvedViewportLayout={getResolvedViewportLayout!}
      rowDataLayout={rowDataLayout!}
      datatip={props.datatip}
      selectedIds={props.selectedIds}
      selectionMode={props.selectionMode}
      onSelectionChange={props.onSelectionChange}
      hoveredTaskId={hoveredTaskId}
      onTaskHoverChange={onTaskHoverChange}
      currentTask={currentTask}
      onCurrentTaskChange={onCurrentTaskChange}
      currentTaskInteraction={currentTaskInteraction}
      ariaActive={ariaActive?.type === 'task' ? ariaActive : undefined}
    />
  );

  const ganttViewWithRowAxis = withRowAxisLayout && (
    <GanttWithRowAxisLayout
      {...withRowAxisLayout}
      rowAxis={(resolvedWidth, resolvedMaxWidth) => (
        <GanttRowAxis
          innerRef={rowAxisRef}
          resolvedWidth={resolvedWidth}
          resolvedMaxWidth={resolvedMaxWidth}
          currentRowLabel={currentRowLabel}
          hoveredRowLabelId={hoveredRowLabelId}
          onRowLabelHoverChange={onRowLabelHoverChange}
          currentRowLabelInteraction={currentRowLabelInteraction}
          ariaActive={ariaActive?.type === 'rowLabel' ? ariaActive : undefined}
          layoutObj={rowAxisLayout}
          scrollPosition={scrollPosition}>
          {withRowAxisLayout.label}
        </GanttRowAxis>
      )}
      ganttView={ganttView}
    />
  );

  return (
    <GanttContainer
      innerRef={ref}
      width={width}
      height={height}
      testId={testId}
      {...ariaProps}
      {...mergeProps(keyboardProps, eventProps)}
      aria-activedescendant={ariaActive?.ariaId}>
      {ganttViewWithRowAxis || ganttView}
      {layoutContent}
    </GanttContainer>
  );
}
